export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

export type TtsVoice = {
  voice_id: string
  name: string
  description: string
}

export type SystemTtsConfig = {
  systemUserId: string
  voices: TtsVoice[]
  thingsToTalkAboutToday: string
  thingsToTalkAboutTodayDate: string
}

export const dbService = {
  async getTtsConfig(): Promise<SystemTtsConfig> {
    const response = await fetch(`${API_BASE_URL}/api/system/tts-config`, {
      credentials: 'include'
    })
    if (!response.ok) throw new Error('Failed to fetch TTS config')
    return await response.json()
  },
  async updateTtsConfig(payload: { voices: TtsVoice[]; thingsToTalkAboutToday: string }): Promise<SystemTtsConfig> {
    const response = await fetch(`${API_BASE_URL}/api/system/tts-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    })
    if (!response.ok) throw new Error('Failed to save TTS config')
    return await response.json()
  },
  async saveUserMissionHistory(userId: string, runRecord: any) {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/mission-history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(runRecord)
    });
    if (!response.ok) throw new Error('Failed to save mission history');
    return await response.json();
  },
  async getUser(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch user');
    return await response.json();
  },
  async getUsers(family_id?: string) {
    const url = family_id ? `${API_BASE_URL}/api/users?family_id=${encodeURIComponent(family_id)}` : `${API_BASE_URL}/api/users`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch users');
    return await response.json();
  },
  async getMissions(family_id?: string) {
    const url = family_id ? `${API_BASE_URL}/api/missions?family_id=${encodeURIComponent(family_id)}` : `${API_BASE_URL}/api/missions`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch missions');
    return await response.json();
  },
  async getMission(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/missions/${id}`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch mission');
    return await response.json();
  },
  async addMission(payload: any) {
    const response = await fetch(`${API_BASE_URL}/api/missions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('Failed to create mission');
    return await response.json();
  },
  async updateMission(payload: any) {
    const response = await fetch(`${API_BASE_URL}/api/missions/${payload.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('Failed to update mission');
    return await response.json();
  },
  async deleteMission(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/missions/${id}`, { 
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to delete mission');
    return await response.json();
  },
  async updateUser(id: string, payload: any) {
    const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('Failed to update user');
    return await response.json();
  },
  async addUser(payload: any) {
    const response = await fetch(`${API_BASE_URL}/api/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('Failed to create user');
    return await response.json();
  },
  async deleteUser(id: string) {
    const response = await fetch(`${API_BASE_URL}/api/users/${id}`, { 
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to delete user');
    return await response.json();
  },
  async addUserActivity(userId: string, type: string, timestamp: string, details: any) {
    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type, timestamp, details })
    });
    if (!response.ok) throw new Error('Failed to add user activity');
    return await response.json();
  },
};

export interface User {
  id: string
  username: string
  email?: string
  password?: string
  /** user type: parent, child, or family (optional) */
  type?: 'parent' | 'child' | 'family'
  /** optional profile image stored as a data URL */
  profileImageDataUrl?: string | null
  /** optional avatar image URL from cloud */
  avatar_url?: string | null
  /** optional stored passkey credential id (base64) */
  passkeyId?: string
  /** whether this user uses a passkey for authentication */
  usePasskey?: boolean
  /** id of the account holder of this user's family (if any) */
  family_id?: string
  createdAt: Date
}



export interface MissionStep {
  id: string;
  name: string;
}

export interface Mission {
  id: string;
  name: string;
  totalTime: number; // in seconds
  steps: MissionStep[];
  createdAt: Date;
  user_id?: string; // assigned child user ID
  family_id?: string; // link mission to family account holder
  history?: Array<{
    runDate: string | number;
    elapsedTime: number;
    steps: Array<{ name: string; elapsedTime: number }>;
  }>;
}
// ...existing code...

// Earworm event and status types
export interface EarwormEvent {
  text: string
  time: string | number
}

export interface EarwormStatus {
  lastevent: EarwormEvent
  events: EarwormEvent[]
}

// Main Earworm type
export interface TranscriptWord {
  word: string
  start?: number
  end?: number
  startS?: number
  endS?: number
  [key: string]: unknown
}

export interface TranscriptData {
  alignedWords?: TranscriptWord[]
  rawLrc?: string
  lines?: Array<{ time?: number; text?: string }>
  [key: string]: unknown
}

export interface Earworm {
  id: string
  user_id: string | null
  name: string
  image_url?: string | null
  data?: any
  analysis?: any
  lyrics?: string | null
  transcript?: TranscriptData | null
  audio_url?: string | null
  status?: string | EarwormStatus | null
  created_at?: string | Date | null
  modified_at?: string | Date | null
  downloaded_at?: string | Date | null
  // Activity stats for this earworm
  userPlays?: number
  userVictories?: number
}
